import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Handshake, Plus, Search, Edit, Trash2, X, Check, Loader2,
  Upload, Users, TrendingUp, Globe, Phone, Mail, MapPin, ExternalLink,
  ArrowLeft, Building2, FileText, ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PARTNER_TYPES = [
  { id: 'apporteur', label: "Apporteur d'affaires", color: '#3B82F6' },
  { id: 'revendeur', label: 'Revendeur', color: '#8B5CF6' },
  { id: 'prescripteur', label: 'Prescripteur', color: '#F59E0B' },
  { id: 'partenaire_technique', label: 'Partenaire technique', color: '#10B981' },
  { id: 'autre', label: 'Autre', color: '#8C8F94' },
];

const getTypeInfo = (t) => PARTNER_TYPES.find(p => p.id === t) || PARTNER_TYPES[4];

export default function Partners() {
  const { api, isSuperAdmin } = useAuth();
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [assignModal, setAssignModal] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const emptyForm = { name: '', contact_name: '', contact_email: '', contact_phone: '', contact_position: '', address: '', city: '', postal_code: '', country: 'France', type: 'apporteur', commission_percent: '', commission_notes: '', website: '', siret: '', notes: '', status: 'active' };
  const [form, setForm] = useState(emptyForm);

  const fetch = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get('/partners'), api.get('/partners/stats')]);
      setPartners(p.data);
      setStats(s.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetch(); }, [fetch]);

  const fetchDetail = async (id) => {
    try {
      const res = await api.get(`/partners/${id}`);
      setSelected(res.data);
    } catch { toast.error('Erreur chargement'); }
  };

  const resetForm = () => { setForm(emptyForm); setEditing(null); setShowForm(false); };

  const openEdit = (p) => {
    setForm({ name: p.name, contact_name: p.contact_name || '', contact_email: p.contact_email || '', contact_phone: p.contact_phone || '', contact_position: p.contact_position || '', address: p.address || '', city: p.city || '', postal_code: p.postal_code || '', country: p.country || 'France', type: p.type || 'apporteur', commission_percent: p.commission_percent ?? '', commission_notes: p.commission_notes || '', website: p.website || '', siret: p.siret || '', notes: p.notes || '', status: p.status || 'active' });
    setEditing(p);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const payload = { ...form, commission_percent: form.commission_percent === '' ? null : parseFloat(form.commission_percent) };
      if (editing) {
        await api.put(`/partners/${editing.id}`, payload);
        toast.success('Partenaire mis a jour');
        if (selected?.id === editing.id) fetchDetail(editing.id);
      } else {
        const res = await api.post('/partners', payload);
        toast.success('Partenaire cree');
        if (res.data?.id) fetchDetail(res.data.id);
      }
      resetForm();
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  const deletePartner = async (p) => {
    if (!window.confirm(`Supprimer ${p.name} ?`)) return;
    try {
      await api.delete(`/partners/${p.id}`);
      toast.success('Supprime');
      if (selected?.id === p.id) setSelected(null);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const uploadLogo = async (partnerId, file) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/partners/${partnerId}/logo`, fd, { 'Content-Type': 'multipart/form-data' });
      toast.success('Logo uploade');
      fetchDetail(partnerId);
      fetch();
    } catch { toast.error('Erreur upload'); } finally { setUploadingLogo(false); }
  };

  const openAssign = async () => {
    try {
      const res = await api.get('/clients');
      setAllClients(res.data);
      setAssignModal(true);
      setClientSearch('');
    } catch { toast.error('Erreur chargement clients'); }
  };

  const assignClient = async (clientId) => {
    try {
      await api.post('/partners/assign-client', { client_id: clientId, partner_id: selected.id });
      toast.success('Client attribue');
      fetchDetail(selected.id);
      setAssignModal(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const unassignClient = async (clientId) => {
    try {
      await api.post('/partners/assign-client', { client_id: clientId, partner_id: null });
      toast.success('Client retire');
      fetchDetail(selected.id);
    } catch { toast.error('Erreur'); }
  };

  const filtered = partners.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return p.name?.toLowerCase().includes(s) || p.contact_name?.toLowerCase().includes(s) || p.city?.toLowerCase().includes(s);
  });

  const fmt = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(v || 0);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00E01A]" /></div>;

  // ===== DETAIL VIEW =====
  if (selected) {
    const ti = getTypeInfo(selected.type);
    return (
      <div className="p-4 md:p-6 space-y-6" data-testid="partner-detail">
        <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-[#8C8F94] hover:text-white text-sm" data-testid="back-to-list">
          <ArrowLeft className="w-4 h-4" /> Retour au reseau
        </button>
        {/* Header */}
        <div className="bg-[#1D2327] border border-[#2C3338] rounded-2xl p-6">
          <div className="flex items-start gap-5">
            <div className="relative group">
              <div className="w-20 h-20 rounded-xl bg-[#141617] border border-[#2C3338] flex items-center justify-center overflow-hidden">
                {selected.logo_url ? (
                  <img src={`${API}${selected.logo_url}`} alt={selected.name} className="w-full h-full object-contain p-2" />
                ) : (
                  <Handshake className="w-8 h-8 text-[#2C3338]" />
                )}
              </div>
              <label className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 rounded-xl cursor-pointer transition-opacity">
                {uploadingLogo ? <Loader2 className="w-5 h-5 animate-spin text-white" /> : <Upload className="w-5 h-5 text-white" />}
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadLogo(selected.id, e.target.files[0])} />
              </label>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold text-white">{selected.name}</h1>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${ti.color}15`, color: ti.color }}>{ti.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${selected.status === 'active' ? 'bg-[#00E01A]/15 text-[#00E01A]' : 'bg-red-500/15 text-red-400'}`}>{selected.status === 'active' ? 'Actif' : 'Inactif'}</span>
              </div>
              {selected.contact_name && <p className="text-[#8C8F94] text-sm">{selected.contact_name}{selected.contact_position && ` — ${selected.contact_position}`}</p>}
              <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-[#8C8F94]">
                {selected.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{selected.contact_email}</span>}
                {selected.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{selected.contact_phone}</span>}
                {selected.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{selected.city}</span>}
                {selected.website && <a href={selected.website.startsWith('http') ? selected.website : `https://${selected.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#3B82F6] hover:underline"><Globe className="w-3 h-3" />{selected.website}</a>}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openEdit(selected)} className="p-2 text-[#8C8F94] hover:text-white hover:bg-white/5 rounded-lg" data-testid="edit-partner"><Edit className="w-4 h-4" /></button>
              {isSuperAdmin() && <button onClick={() => deletePartner(selected)} className="p-2 text-[#8C8F94] hover:text-red-400 hover:bg-red-500/10 rounded-lg" data-testid="delete-partner"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-4">
            <p className="text-[#8C8F94] text-xs mb-1">Clients attribues</p>
            <p className="text-2xl font-bold text-white">{selected.clients_count || 0}</p>
          </div>
          <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-4">
            <p className="text-[#8C8F94] text-xs mb-1">CA genere</p>
            <p className="text-2xl font-bold text-[#00E01A]">{fmt(selected.total_ca)}</p>
          </div>
          <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-4">
            <p className="text-[#8C8F94] text-xs mb-1">Commission</p>
            <p className="text-2xl font-bold text-white">{selected.commission_percent != null ? `${selected.commission_percent}%` : '—'}</p>
          </div>
          <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-4">
            <p className="text-[#8C8F94] text-xs mb-1">SIRET</p>
            <p className="text-sm font-medium text-white truncate">{selected.siret || '—'}</p>
          </div>
        </div>

        {/* Info + Notes */}
        {(selected.commission_notes || selected.notes || selected.address) && (
          <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-5 space-y-3">
            {selected.address && <div><p className="text-xs text-[#8C8F94] mb-0.5">Adresse</p><p className="text-sm text-white">{selected.address}{selected.postal_code && `, ${selected.postal_code}`} {selected.city}</p></div>}
            {selected.commission_notes && <div><p className="text-xs text-[#8C8F94] mb-0.5">Conditions commission</p><p className="text-sm text-white whitespace-pre-wrap">{selected.commission_notes}</p></div>}
            {selected.notes && <div><p className="text-xs text-[#8C8F94] mb-0.5">Notes</p><p className="text-sm text-white whitespace-pre-wrap">{selected.notes}</p></div>}
          </div>
        )}

        {/* Clients */}
        <div className="bg-[#1D2327] border border-[#2C3338] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold">Clients attribues ({selected.clients?.length || 0})</h3>
            <button onClick={openAssign} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00E01A] text-black text-xs font-semibold rounded-lg hover:bg-[#00B815]" data-testid="assign-client-btn">
              <Plus className="w-3.5 h-3.5" /> Attribuer un client
            </button>
          </div>
          {selected.clients?.length > 0 ? (
            <div className="space-y-2">
              {selected.clients.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-[#141617] rounded-lg border border-[#2C3338]">
                  <div>
                    <p className="text-white text-sm font-medium">{c.full_name || `${c.first_name} ${c.last_name}`}</p>
                    <p className="text-[#8C8F94] text-xs">{c.email || c.phone || '—'} {c.city && `• ${c.city}`}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[#00E01A] text-sm font-semibold">{fmt(c.total_ca || 0)}</span>
                    <button onClick={() => unassignClient(c.id)} className="p-1.5 text-[#8C8F94] hover:text-red-400 hover:bg-red-500/10 rounded" data-testid={`unassign-${c.id}`}><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#8C8F94] text-sm text-center py-6">Aucun client attribue</p>
          )}
        </div>

        {/* Assign Client Modal */}
        {assignModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setAssignModal(false)}>
            <div className="bg-[#1D2327] border border-[#2C3338] rounded-2xl w-full max-w-lg p-6 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="assign-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Attribuer un client</h3>
                <button onClick={() => setAssignModal(false)} className="p-1.5 text-[#8C8F94] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8F94]" />
                <input value={clientSearch} onChange={e => setClientSearch(e.target.value)} placeholder="Rechercher un client..." className="w-full pl-10 pr-4 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="client-search-assign" />
              </div>
              <div className="flex-1 overflow-y-auto space-y-1">
                {allClients.filter(c => {
                  if (selected.clients?.some(sc => sc.id === c.id)) return false;
                  if (!clientSearch) return true;
                  const s = clientSearch.toLowerCase();
                  return c.full_name?.toLowerCase().includes(s) || c.first_name?.toLowerCase().includes(s) || c.last_name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s);
                }).map(c => (
                  <button key={c.id} onClick={() => assignClient(c.id)} className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-[#141617] text-left transition-colors" data-testid={`assign-${c.id}`}>
                    <div>
                      <p className="text-white text-sm">{c.full_name || `${c.first_name} ${c.last_name}`}</p>
                      <p className="text-[#8C8F94] text-xs">{c.email || '—'}</p>
                    </div>
                    <span className="text-[#00E01A] text-xs">{fmt(c.total_ca || 0)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Edit Form Modal */}
        {showForm && <PartnerFormModal form={form} setForm={setForm} editing={editing} saving={saving} onSubmit={handleSubmit} onClose={resetForm} />}
      </div>
    );
  }

  // ===== LIST VIEW =====
  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="partners-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Reseau</h1>
          <p className="text-[#8C8F94] mt-1">Partenaires et apporteurs d'affaires</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-xl text-sm" data-testid="add-partner-btn">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><Handshake className="w-4 h-4 text-[#00E01A]" /><span className="text-xs text-[#8C8F94]">Partenaires</span></div>
          <p className="text-2xl font-bold text-white">{stats.total || 0}</p>
        </div>
        <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><Check className="w-4 h-4 text-[#00E01A]" /><span className="text-xs text-[#8C8F94]">Actifs</span></div>
          <p className="text-2xl font-bold text-white">{stats.active || 0}</p>
        </div>
        <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-[#3B82F6]" /><span className="text-xs text-[#8C8F94]">Clients attribues</span></div>
          <p className="text-2xl font-bold text-white">{stats.clients_with_partner || 0}</p>
        </div>
        <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-[#00E01A]" /><span className="text-xs text-[#8C8F94]">CA reseau</span></div>
          <p className="text-2xl font-bold text-[#00E01A]">{fmt(stats.total_ca)}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8F94]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un partenaire..." className="w-full pl-10 pr-4 py-2.5 bg-[#1D2327] border border-[#2C3338] rounded-xl text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="partner-search" />
      </div>

      {/* Partner List */}
      <div className="space-y-2">
        {filtered.map(p => {
          const ti = getTypeInfo(p.type);
          return (
            <button key={p.id} onClick={() => fetchDetail(p.id)} className="w-full bg-[#1D2327] border border-[#2C3338] hover:border-[#00E01A]/30 rounded-xl p-4 flex items-center gap-4 transition-all text-left" data-testid={`partner-card-${p.id}`}>
              <div className="w-12 h-12 rounded-lg bg-[#141617] border border-[#2C3338] flex items-center justify-center overflow-hidden flex-shrink-0">
                {p.logo_url ? <img src={`${API}${p.logo_url}`} alt={p.name} className="w-full h-full object-contain p-1" /> : <Handshake className="w-5 h-5 text-[#2C3338]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium text-sm truncate">{p.name}</p>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0" style={{ backgroundColor: `${ti.color}15`, color: ti.color }}>{ti.label}</span>
                </div>
                <p className="text-[#8C8F94] text-xs truncate">{p.contact_name || '—'} {p.city && `• ${p.city}`}</p>
              </div>
              <div className="text-right flex-shrink-0 hidden md:block">
                <p className="text-[#00E01A] font-semibold text-sm">{fmt(p.total_ca)}</p>
                <p className="text-[#8C8F94] text-xs">{p.clients_count} client{p.clients_count > 1 ? 's' : ''}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#8C8F94] flex-shrink-0" />
            </button>
          );
        })}
        {filtered.length === 0 && <p className="text-[#8C8F94] text-sm text-center py-8">Aucun partenaire{search ? ' trouve' : ''}</p>}
      </div>

      {showForm && <PartnerFormModal form={form} setForm={setForm} editing={editing} saving={saving} onSubmit={handleSubmit} onClose={resetForm} />}
    </div>
  );
}

function PartnerFormModal({ form, setForm, editing, saving, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1D2327] border border-[#2C3338] rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="partner-form-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">{editing ? 'Modifier le partenaire' : 'Nouveau partenaire'}</h3>
          <button onClick={onClose} className="p-1.5 text-[#8C8F94] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-[#8C8F94] mb-1">Nom / Raison sociale *</label>
            <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" required data-testid="partner-name" />
          </div>
          <div>
            <label className="block text-xs text-[#8C8F94] mb-1">Type de partenariat</label>
            <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="partner-type">
              {PARTNER_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#8C8F94] mb-1">Contact</label>
              <input value={form.contact_name} onChange={e => setForm(f => ({...f, contact_name: e.target.value}))} placeholder="Nom complet" className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="partner-contact" />
            </div>
            <div>
              <label className="block text-xs text-[#8C8F94] mb-1">Poste</label>
              <input value={form.contact_position} onChange={e => setForm(f => ({...f, contact_position: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#8C8F94] mb-1">Email</label>
              <input type="email" value={form.contact_email} onChange={e => setForm(f => ({...f, contact_email: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="partner-email" />
            </div>
            <div>
              <label className="block text-xs text-[#8C8F94] mb-1">Telephone</label>
              <input value={form.contact_phone} onChange={e => setForm(f => ({...f, contact_phone: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="partner-phone" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-[#8C8F94] mb-1">Ville</label>
              <input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-[#8C8F94] mb-1">Commission %</label>
              <input type="number" step="0.5" value={form.commission_percent} onChange={e => setForm(f => ({...f, commission_percent: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="partner-commission" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#8C8F94] mb-1">SIRET</label>
            <input value={form.siret} onChange={e => setForm(f => ({...f, siret: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-[#8C8F94] mb-1">Site web</label>
            <input value={form.website} onChange={e => setForm(f => ({...f, website: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-[#8C8F94] mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none resize-none" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#2C3338] text-[#8C8F94] rounded-xl text-sm hover:text-white">Annuler</button>
            <button type="submit" disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00E01A] text-black font-semibold rounded-xl text-sm hover:bg-[#00B815] disabled:opacity-50" data-testid="partner-form-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {editing ? 'Enregistrer' : 'Creer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
